import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { LANGUAGES } from '../i18n';

export function LanguageSelector() {
    const { i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    const current = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES.find(l => i18n.language?.startsWith(l.code)) || LANGUAGES[0];

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const switchLang = (code) => {
        i18n.changeLanguage(code);
        localStorage.setItem('bet4peer_lang', code);
        setOpen(false);
    };

    return (
        <div className="relative" ref={ref} data-testid="language-selector" role="listbox" aria-label="Language selector">
            <button
                onClick={() => setOpen(!open)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 hover:border-white/20 transition-colors text-sm"
                data-testid="language-selector-btn"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={`Language: ${current.name}. Click to change`}
            >
                <Globe className="w-3.5 h-3.5 text-white/50" />
                <span className="text-white/70 text-xs font-medium hidden sm:inline">{current.name}</span>
                <span className="text-white/70 text-xs font-medium sm:hidden">{current.code.toUpperCase()}</span>
                <ChevronDown className={`w-3 h-3 text-white/30 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
                <div className="absolute right-0 top-full mt-2 w-56 max-h-80 overflow-y-auto rounded-xl bg-popover border border-border shadow-2xl z-[100] py-1 animate-fade-in-down" data-testid="language-dropdown" role="listbox" aria-label="Available languages">
                    {LANGUAGES.map(lang => (
                        <button
                            key={lang.code}
                            onClick={() => switchLang(lang.code)}
                            className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors ${
                                current.code === lang.code ? 'bg-[#00FF66]/10 text-[#00FF66]' : 'text-foreground/70 hover:bg-muted hover:text-foreground'
                            }`}
                            data-testid={`lang-${lang.code}`}
                            role="option"
                            aria-selected={current.code === lang.code}
                            aria-label={`Switch to ${lang.name}`}
                        >
                            <span className="w-5 text-center text-xs">{lang.flag}</span>
                            <span className="flex-1">{lang.name}</span>
                            {current.code === lang.code && <Check className="w-3.5 h-3.5 text-[#00FF66]" />}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
